import type { GetConversationsSubscription } from '@/__generated__/graphql';
import { useGetConversationsSubscription } from '@/__generated__/graphql';
import { getAuthRoleHeader } from '@/lib/apollo';
import { useTreatyClient } from '@/lib/client';
import { useAuth } from '@/stores/AuthContext';
import { Button, Input, Modal, Typography } from 'antd';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';

interface ConversationListProps {
  selectedId: string | null;
  onSelect: (id: string) => void;
}

type ConversationItem = GetConversationsSubscription['conversations'][number];

export const ConversationList = observer(
  ({ selectedId, onSelect }: ConversationListProps) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const client = useTreatyClient();
    const { user } = useAuth();

    const { data, loading } = useGetConversationsSubscription({
      skip: !user,
      shouldResubscribe: true,
      context: getAuthRoleHeader('user'),
    });

    // Sort conversations locally to ensure correct ordering
    const sortedConversations = useMemo(() => {
      if (!data?.conversations) return [];

      return [...data.conversations].sort((a, b) => {
        const aTime = a.messages[0]?.created_at || a.created_at;
        const bTime = b.messages[0]?.created_at || b.created_at;
        return new Date(bTime).getTime() - new Date(aTime).getTime();
      });
    }, [data?.conversations]);

    const onCreateNew = useCallback(async () => {
      if (!newTitle.trim()) return;

      try {
        setIsCreating(true);
        const response = await client.api.chat.conversations.create.post({
          title: newTitle.trim(),
        });
        if (response.data) {
          setIsDialogOpen(false);
          setNewTitle('');
        }
      } catch (error) {
        console.error('Failed to create new conversation:', error);
      } finally {
        setIsCreating(false);
      }
    }, [newTitle, client]);

    return (
      <div className="flex flex-col h-full">
        <div className="mb-4">
          <Button
            type="primary"
            className="w-full"
            onClick={() => setIsDialogOpen(true)}
            size="large"
          >
            New Conversation
          </Button>
        </div>
        <div className="flex-1 overflow-auto">
          <div className="space-y-2">
            {loading ? (
              <div className="text-center py-4 text-muted-foreground">
                Loading conversations...
              </div>
            ) : !sortedConversations?.length ? (
              <div className="text-center py-4 text-muted-foreground">
                No conversations yet
              </div>
            ) : (
              sortedConversations.map((conversation: ConversationItem) => (
                <button
                  key={conversation.id}
                  type="button"
                  onClick={() => onSelect(conversation.id)}
                  className={`w-full p-3 text-left rounded-lg hover:bg-muted transition-colors ${
                    selectedId === conversation.id ? 'bg-muted' : ''
                  }`}
                >
                  <div className="font-medium truncate">
                    {conversation.title}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(
                      conversation.messages[0]?.created_at ||
                        conversation.created_at,
                    ).toLocaleString(undefined, {
                      year: 'numeric',
                      month: 'numeric',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false,
                    })}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        <Modal
          title="New Conversation"
          open={isDialogOpen}
          onCancel={() => setIsDialogOpen(false)}
          style={{ top: '30%' }}
          footer={[
            <Button
              key="create"
              type="primary"
              onClick={onCreateNew}
              loading={isCreating}
              disabled={!newTitle.trim() || isCreating}
            >
              Create
            </Button>,
          ]}
        >
          <div>
            <p className="text-sm text-gray-500 mb-4">
              Enter the name of your new conversation
            </p>
            <div className="space-y-2">
              <Typography.Text>Name</Typography.Text>
              <Input
                id="title"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Enter conversation name"
                disabled={isCreating}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    onCreateNew();
                  }
                }}
              />
            </div>
          </div>
        </Modal>
      </div>
    );
  },
);
